import { useEffect, useRef, useState } from 'react'

/**
 * Выпадающий список с чекбоксами — прикрепление помощника (роль ASSISTANT)
 * к менеджерам (MANAGER/ROP). Используется в строке аккаунта на AccountsPage.
 * Сохранение — по закрытию дропдауна (одним PATCH), чтобы не слать запрос
 * на каждый клик по чекбоксу.
 */
export interface AssistantManagerOption {
  id: string
  name: string | null
  email: string
  role: string
}

export function AssistantManagersDropdown({
  selected,
  options,
  onSave,
}: {
  selected: string[]
  options: AssistantManagerOption[]
  onSave: (managerIds: string[]) => void
}) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<Set<string>>(new Set(selected))
  const rootRef = useRef<HTMLDivElement>(null)

  // Синхронизация при обновлении данных с сервера (пока дропдаун закрыт).
  useEffect(() => {
    if (!open) setDraft(new Set(selected))
  }, [selected, open])

  // Закрытие по клику вне — с сохранением, если набор изменился.
  useEffect(() => {
    if (!open) return
    const onClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        commitAndClose()
      }
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draft])

  const commitAndClose = () => {
    setOpen(false)
    const next = [...draft].sort()
    const prev = [...selected].sort()
    if (next.join(',') !== prev.join(',')) onSave(next)
  }

  const toggle = (id: string) => {
    setDraft((cur) => {
      const next = new Set(cur)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const label = (() => {
    if (selected.length === 0) return 'Не прикреплён'
    const names = options.filter((o) => selected.includes(o.id)).map((o) => o.name || o.email)
    if (names.length === 0) return 'Не прикреплён'
    if (names.length === 1) return names[0]
    return `${names[0]} +${names.length - 1}`
  })()

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => (open ? commitAndClose() : setOpen(true))}
        className={`text-xs px-2 py-1 rounded border border-gray-200 hover:border-brand-400 focus:outline-none focus:border-brand-500 bg-white cursor-pointer max-w-[180px] truncate ${
          selected.length === 0 ? 'text-amber-600' : 'text-gray-700'
        }`}
        title="Менеджеры, которым помогает этот аккаунт"
      >
        {label} ▾
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-64 max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg p-2 space-y-0.5">
          <div className="px-2 py-1 text-[11px] uppercase tracking-wide text-gray-400">
            Помогает менеджерам
          </div>
          {options.length === 0 ? (
            <div className="px-2 py-2 text-xs text-gray-400">
              Нет активных менеджеров/РОПов для прикрепления
            </div>
          ) : (
            options.map((o) => (
              <label
                key={o.id}
                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={draft.has(o.id)}
                  onChange={() => toggle(o.id)}
                  className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                />
                <span className="text-sm text-gray-800 truncate">{o.name || o.email}</span>
                {o.role === 'ROP' && <span className="text-[10px] text-indigo-500">РОП</span>}
              </label>
            ))
          )}
          <div className="pt-1 border-t border-gray-100 flex justify-end">
            <button
              type="button"
              onClick={commitAndClose}
              className="text-xs px-3 py-1 rounded bg-brand-600 text-white hover:bg-brand-700"
            >
              Готово
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
